import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Icon } from "../lib/Icon";
import { Avatar } from "../lib/components";
import { useCatalog } from "../contexts/CatalogContext";
import { useToast } from "../lib/toast";
import {
  type AdminAuditRow,
  type AdminListingRow,
  type AdminMetrics,
  type AdminTradeRow,
  type AdminUserRow,
  type CountryReportRow,
  type ReportsGlobalSummary,
  type StickerReportRow,
  type TopCollectorRow,
  banUser,
  bulkUpdateStickers,
  deleteUser,
  fetchActiveListings,
  fetchAuditLog,
  fetchMetrics,
  fetchMostDuped,
  fetchMostNeeded,
  fetchRecentTrades,
  fetchReportsByCountry,
  fetchReportsGlobalSummary,
  fetchTopCollectors,
  fetchUsers,
  patchSticker,
  unbanUser,
} from "../api/admin";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import type { Sticker } from "../api/catalog";

type AdminTab = "metrics" | "users" | "catalog" | "trades" | "market" | "reports" | "abuses" | "audit";

const TABS: { k: AdminTab; label: string; icon: string }[] = [
  { k: "metrics", label: "Métricas", icon: "sparkle" },
  { k: "users", label: "Usuarios", icon: "user" },
  { k: "catalog", label: "Catálogo", icon: "grid" },
  { k: "trades", label: "Intercambios", icon: "swap" },
  { k: "market", label: "Mercado", icon: "store" },
  { k: "reports", label: "Reportes", icon: "sparkle" },
  { k: "abuses", label: "Denuncias", icon: "flame" },
  { k: "audit", label: "Auditoría", icon: "filter" },
];

interface AdminScreenProps {
  openChat: (userId: number) => void;
}

export function AdminScreen({ openChat }: AdminScreenProps) {
  const [tab, setTab] = useState<AdminTab>("metrics");

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="chip" style={{ marginBottom: 6 }}>
            <Icon name="settings" size={11} /> Panel Nilabs
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
            Centro de control
          </h1>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>
            Métricas, usuarios y operaciones de PaniniNilabs.
          </p>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.k} className={"tab " + (tab === t.k ? "active" : "")} onClick={() => setTab(t.k)}>
            <Icon name={t.icon} size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "metrics" && <MetricsTab />}
      {tab === "users" && <UsersTab openChat={openChat} />}
      {tab === "catalog" && <CatalogTab />}
      {tab === "trades" && <TradesTab />}
      {tab === "market" && <MarketTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "abuses" && <AbusesTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

// Tab de DENUNCIAS (reports de abuso de un user contra otro). El tab "Reportes"
// (estadísticas) está más abajo y se llama ReportsTab.
function AbusesTab() {
  const [rows, setRows] = useState<Array<{
    id: number; reporter_id: number; reported_user_id: number;
    reason: string; details: string | null; status: string;
    created_at: string; resolved_at: string | null;
  }>>([]);
  const [statusFilter, setStatusFilter] = useState<"open" | "reviewing" | "closed">("open");
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api<typeof rows>(`/admin/reports?status_filter=${statusFilter}&limit=100`);
      setRows(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const resolve = async (id: number, action: "closed" | "reviewing") => {
    try {
      await api(`/reports/${id}/resolve?action=${action}`, { method: "POST" });
      toast(action === "closed" ? "Reporte cerrado" : "Marcado como en revisión");
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {(["open", "reviewing", "closed"] as const).map((s) => (
          <button
            key={s}
            className={"tab " + (statusFilter === s ? "active" : "")}
            onClick={() => setStatusFilter(s)}
          >
            {s === "open" ? "Abiertos" : s === "reviewing" ? "En revisión" : "Cerrados"}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : rows.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>
          Sin reportes en estado "{statusFilter}".
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                <Th>ID</Th>
                <Th>Reporter</Th>
                <Th>Reportado</Th>
                <Th>Motivo</Th>
                <Th>Detalles</Th>
                <Th>Estado</Th>
                <Th>Fecha</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <Td>#{r.id}</Td>
                  <Td>u{r.reporter_id}</Td>
                  <Td>u{r.reported_user_id}</Td>
                  <Td><b>{r.reason}</b></Td>
                  <Td>
                    <span style={{ display: "inline-block", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.details || "—"}
                    </span>
                  </Td>
                  <Td>
                    <span className={"chip " + (r.status === "open" ? "amber" : r.status === "reviewing" ? "cobalt" : "")}>
                      {r.status}
                    </span>
                  </Td>
                  <Td className="tiny muted">{relativeDate(r.created_at)}</Td>
                  <Td>
                    {r.status !== "closed" && (
                      <div style={{ display: "flex", gap: 6 }}>
                        {r.status === "open" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => resolve(r.id, "reviewing")}>Revisando</button>
                        )}
                        <button className="btn btn-primary btn-sm" onClick={() => resolve(r.id, "closed")}>Cerrar</button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================

function MetricsTab() {
  const [data, setData] = useState<AdminMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics().then(setData).catch((e) => setError(String(e)));
  }, []);

  if (error) return <ErrorBox text={error} />;
  if (!data) return <Loading />;

  const cards: { label: string; value: string | number; sub?: string; color: string }[] = [
    { label: "Usuarios totales", value: data.users_total, sub: `+${data.users_new_7d} en 7d`, color: "var(--primary)" },
    { label: "Activos 30d", value: data.users_active_30d, sub: `${pct(data.users_active_30d, data.users_total)}% del total`, color: "var(--accent-cobalt)" },
    { label: "Intercambios", value: data.trades_total, sub: `${data.trades_completed} completados · ${data.trades_pending} pendientes`, color: "var(--accent-magenta)" },
    { label: "Listings activos", value: data.listings_active, sub: "en el mercado ahora", color: "var(--accent-amber)" },
    { label: "Reseñas (avg)", value: data.reviews_avg_stars?.toFixed(1) ?? "—", sub: "promedio global", color: "var(--primary)" },
    { label: "Reportes abiertos", value: data.reports_open, sub: data.reports_open > 0 ? "requieren atención" : "todo limpio", color: data.reports_open > 0 ? "var(--accent-magenta)" : "var(--fg-muted)" },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginTop: 16 }}>
      {cards.map((c) => (
        <div key={c.label} className="card" style={{ borderTop: `3px solid ${c.color}` }}>
          <div className="stat-label" style={{ marginBottom: 6 }}>{c.label}</div>
          <div className="stat-value" style={{ fontSize: 36 }}>{c.value}</div>
          {c.sub && <div className="tiny muted" style={{ marginTop: 4 }}>{c.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ============================================================================

function UsersTab({ openChat }: { openChat: (userId: number) => void }) {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";

  const reload = () => {
    setLoading(true);
    fetchUsers({ q: q || undefined, limit: 100 })
      .then(setUsers)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBan = async (u: AdminUserRow) => {
    const reason = window.prompt(`Razón para banear a ${u.name}?`, "Violación de TyC");
    if (!reason) return;
    try {
      await banUser(u.id, reason);
      toast(`Usuario ${u.name} baneado`);
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleUnban = async (u: AdminUserRow) => {
    try {
      await unbanUser(u.id);
      toast(`Usuario ${u.name} desbaneado`);
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleDelete = async (u: AdminUserRow) => {
    if (!confirm(
      `⚠️ Eliminar permanentemente a ${u.name} (${u.email})?\n\n` +
      `Esto borra: cuenta, álbum, propuestas, mensajes, listings y reseñas.\n` +
      `NO se puede deshacer.`
    )) return;
    const typed = window.prompt(`Para confirmar, escribe el email del usuario:\n${u.email}`);
    if (typed?.trim().toLowerCase() !== u.email.toLowerCase()) {
      toast("Email no coincide. Operación cancelada.");
      return;
    }
    try {
      await deleteUser(u.id);
      toast(`Usuario ${u.name} eliminado`);
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div className="search-box" style={{ flex: 1, maxWidth: 360 }}>
          <Icon name="search" size={16} />
          <input placeholder="Buscar por nombre/email/handle…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload()} />
        </div>
        <button className="btn btn-secondary" onClick={reload}>
          <Icon name="filter" size={14} /> Refrescar
        </button>
      </div>

      {loading ? <Loading /> : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ minWidth: 1100, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                <Th>Usuario</Th>
                <Th>Email</Th>
                <Th>Rol</Th>
                <Th>% Álbum</Th>
                <Th>Trueques</Th>
                <Th>Estrellas</Th>
                <Th>Estado</Th>
                <Th>Registro</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Avatar user={u} />
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        <div className="tiny muted">{u.handle}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <span style={{ display: "inline-block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={u.email}>
                      {u.email}
                    </span>
                  </Td>
                  <Td>
                    <span className={"chip " + (u.role === "super_admin" ? "primary" : u.role === "admin" ? "cobalt" : "")}>
                      {u.role}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 70 }}>
                      <div style={{ fontWeight: 600 }}>{u.album_pct}%</div>
                      {u.album_dups > 0 && <div className="tiny muted">+{u.album_dups} repes</div>}
                    </div>
                  </Td>
                  <Td>{u.trades_completed}</Td>
                  <Td>
                    <Icon name="star" size={11} /> {u.stars_avg.toFixed(1)}
                  </Td>
                  <Td>
                    {u.is_banned ? <span className="chip magenta">Baneado</span> : <span className="chip">Activo</span>}
                  </Td>
                  <Td className="tiny muted">{relativeDate(u.created_at)}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {isSuperAdmin && u.id !== me?.id && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openChat(u.id)}
                          title={u.is_banned ? "Chat con usuario baneado" : "Iniciar chat con este usuario"}
                        >
                          <Icon name="chat" size={12} /> Chat
                        </button>
                      )}
                      {u.is_banned ? (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleUnban(u)}>Desbanear</button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleBan(u)}>Banear</button>
                      )}
                      {isSuperAdmin && u.role !== "super_admin" && u.id !== me?.id && (
                        <button
                          className="btn btn-sm"
                          onClick={() => handleDelete(u)}
                          style={{ background: "var(--accent-magenta)", color: "white", border: 0 }}
                          title="Eliminar permanentemente"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 32, color: "var(--fg-muted)" }}>Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================

function TradesTab() {
  const [rows, setRows] = useState<AdminTradeRow[]>([]);
  useEffect(() => {
    fetchRecentTrades(50).then(setRows);
  }, []);

  const statusChip = (s: string) => {
    const cls = s === "completed" ? "primary" : s === "pending" ? "amber" : s === "rejected" ? "magenta" : "cobalt";
    return <span className={"chip " + cls}>{s}</span>;
  };

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
            <Th>ID</Th>
            <Th>De</Th>
            <Th>A</Th>
            <Th>Estado</Th>
            <Th>Creado</Th>
            <Th>Completado</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} style={{ borderTop: "1px solid var(--border)" }}>
              <Td>#{t.id}</Td>
              <Td>u{t.from_user_id}</Td>
              <Td>u{t.to_user_id}</Td>
              <Td>{statusChip(t.status)}</Td>
              <Td className="tiny muted">{relativeDate(t.created_at)}</Td>
              <Td className="tiny muted">{t.completed_at ? relativeDate(t.completed_at) : "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================

function MarketTab() {
  const { stickerById } = useCatalog();
  const [rows, setRows] = useState<AdminListingRow[]>([]);
  useEffect(() => {
    fetchActiveListings(100).then(setRows).catch(() => setRows([]));
  }, []);

  if (rows.length === 0) {
    return <div className="card" style={{ marginTop: 16, padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>Aún no hay listings activos en el mercado.</div>;
  }

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
            <Th>ID</Th>
            <Th>Vendedor</Th>
            <Th>Lámina</Th>
            <Th>Tipo</Th>
            <Th>Precio</Th>
            <Th>Termina</Th>
            <Th>Creado</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => {
            const sticker = stickerById(l.sticker_id);
            return (
              <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }}>
                <Td>#{l.id}</Td>
                <Td>u{l.seller_id}</Td>
                <Td>{sticker ? `${sticker.num} · ${sticker.name}` : `#${l.sticker_id}`}</Td>
                <Td><span className="chip">{l.type}</span></Td>
                <Td>{l.price ? `$${l.price.toLocaleString("es-CO")}` : "—"} {l.current_bid ? `(puja $${l.current_bid.toLocaleString("es-CO")})` : ""}</Td>
                <Td className="tiny muted">{l.ends_at ?? "—"}</Td>
                <Td className="tiny muted">{relativeDate(l.created_at)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================

function AuditTab() {
  const [rows, setRows] = useState<AdminAuditRow[]>([]);
  useEffect(() => {
    fetchAuditLog(100).then(setRows);
  }, []);

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
            <Th>Fecha</Th>
            <Th>Admin</Th>
            <Th>Acción</Th>
            <Th>Target</Th>
            <Th>Payload</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
              <Td className="tiny muted">{relativeDate(r.created_at)}</Td>
              <Td>u{r.admin_user_id}</Td>
              <Td><span className="chip">{r.action}</span></Td>
              <Td>{r.target_type ? `${r.target_type}#${r.target_id}` : "—"}</Td>
              <Td className="tiny muted">{r.payload ? JSON.stringify(r.payload) : "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================

function CatalogTab() {
  const { teams, stickers, loading: catLoading } = useCatalog();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Sticker | null>(null);
  const [importJson, setImportJson] = useState<string | null>(null);
  const toast = useToast();

  const filtered = useMemo(() => {
    if (!search.trim()) return stickers.slice(0, 200);
    const q = search.toLowerCase().trim();
    return stickers
      .filter((s) => {
        const team = teams.find((t) => t.id === s.team_id);
        const haystack = `${s.name} ${s.num} ${team?.code ?? ""} ${team?.name ?? ""}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 200);
  }, [stickers, teams, search]);

  if (catLoading) return <Loading />;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div className="search-box" style={{ flex: "1 1 280px" }}>
          <Icon name="search" size={16} />
          <input
            placeholder='Buscar lámina: "POR 12", "Vitinha", "Portugal"…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <button className="btn btn-secondary" onClick={() => setImportJson("")}>
          <Icon name="upload" size={14} /> Importar nombres (JSON)
        </button>
      </div>

      <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 12 }}>
        💡 Click en cualquier lámina para editar el nombre del jugador. Mostrando {filtered.length} de {stickers.length} láminas.
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
              <Th>Número</Th>
              <Th>Equipo</Th>
              <Th>Jugador</Th>
              <Th>Rol</Th>
              <Th>Camiseta</Th>
              <Th>Flags</Th>
              <Th>{""}</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const team = teams.find((t) => t.id === s.team_id);
              return (
                <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <Td><b>{s.num}</b></Td>
                  <Td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 4, height: 14, background: team?.color || "#999", borderRadius: 2, display: "inline-block" }} />
                      {team?.name ?? "—"}
                    </span>
                  </Td>
                  <Td>{s.name}</Td>
                  <Td><span className="chip">{s.role}</span></Td>
                  <Td>{s.shirt ?? "—"}</Td>
                  <Td>
                    {s.is_legendary && <span className="chip amber" style={{ fontSize: 10, marginRight: 4 }}>LEG</span>}
                    {s.is_limited && <span className="chip magenta" style={{ fontSize: 10 }}>LIM</span>}
                    {s.is_shield && <span className="chip" style={{ fontSize: 10 }}>ESCUDO</span>}
                  </Td>
                  <Td>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(s)}>
                      Editar
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <EditStickerModal sticker={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); window.location.reload(); }} />}
      {importJson !== null && <ImportStickersModal initial={importJson} onClose={() => setImportJson(null)} onImported={() => { setImportJson(null); toast("Importación lista — recargá para ver cambios"); }} />}
    </div>
  );
}

interface EditStickerModalProps {
  sticker: Sticker;
  onClose: () => void;
  onSaved: () => void;
}

function EditStickerModal({ sticker, onClose, onSaved }: EditStickerModalProps) {
  const [name, setName] = useState(sticker.name);
  const [role, setRole] = useState(sticker.role);
  const [shirt, setShirt] = useState<string>(sticker.shirt?.toString() ?? "");
  const [isLegendary, setIsLegendary] = useState(sticker.is_legendary);
  const [isLimited, setIsLimited] = useState(sticker.is_limited);
  const [imageUrl, setImageUrl] = useState(sticker.image_url ?? "");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await patchSticker(sticker.id, {
        name: name.trim(),
        role,
        shirt: shirt ? Number(shirt) : undefined,
        is_legendary: isLegendary,
        is_limited: isLimited,
        image_url: imageUrl.trim(),
      });
      toast(`Lámina ${sticker.num} actualizada`);
      onSaved();
    } catch (err) {
      toast(`Error: ${err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 480, width: "100%", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, margin: 0 }}>
            Editar {sticker.num}
          </h2>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>
        <form onSubmit={save} className="col" style={{ gap: 14 }}>
          <div className="field">
            <label>Nombre del jugador / lámina</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Vitinha" autoFocus />
          </div>
          <div className="field">
            <label>Rol</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option>Escudo</option>
              <option>Foto grupal</option>
              <option>Arquero</option>
              <option>Defensa</option>
              <option>Mediocampo</option>
              <option>Delantero</option>
              <option>Capitán</option>
              <option>Estrella</option>
              <option>Estadio</option>
              <option>Mascota</option>
              <option>Trofeo</option>
            </select>
          </div>
          <div className="field">
            <label>Número de camiseta (opcional)</label>
            <input type="number" min={0} max={99} value={shirt} onChange={(e) => setShirt(e.target.value)} placeholder="Ej: 12" />
          </div>
          <div className="field">
            <label>URL de imagen (opcional)</label>
            <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://upload.wikimedia.org/..." />
            {imageUrl && (
              <div style={{ marginTop: 8, padding: 8, background: "var(--bg-subtle)", borderRadius: 8, display: "flex", justifyContent: "center" }}>
                <img src={imageUrl} alt="preview" style={{ maxHeight: 120, borderRadius: 6 }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              </div>
            )}
            <div className="hint">URL pública (Wikipedia/Wikimedia recomendado por licencia CC).</div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
              <input type="checkbox" checked={isLegendary} onChange={(e) => setIsLegendary(e.target.checked)} />
              Legendaria (rara)
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
              <input type="checkbox" checked={isLimited} onChange={(e) => setIsLimited(e.target.checked)} />
              Edición limitada
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ImportStickersModalProps {
  initial: string;
  onClose: () => void;
  onImported: () => void;
}

function ImportStickersModal({ initial, onClose, onImported }: ImportStickersModalProps) {
  const [text, setText] = useState(initial);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState<{ updated: number; skipped: number; errors: string[] } | null>(null);
  const toast = useToast();

  const submit = async () => {
    setWorking(true);
    setResult(null);
    try {
      const items = JSON.parse(text);
      if (!Array.isArray(items)) throw new Error("Esperaba un array de objetos");
      const r = await bulkUpdateStickers(items);
      setResult(r);
      if (r.updated > 0) onImported();
    } catch (err) {
      toast(`Error: ${err instanceof Error ? err.message : err}`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 640, width: "100%", padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, margin: 0 }}>Importar nombres en bulk</h2>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>
        <p style={{ fontSize: 13, color: "var(--fg-muted)", margin: "0 0 12px" }}>
          Pega un JSON con un array de objetos. Cada objeto identifica la lámina por <code>num</code> (ej. "POR-12") y opcionalmente actualiza <code>name</code>, <code>role</code>, <code>shirt</code>, <code>is_legendary</code>.
        </p>
        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--primary)" }}>Ver ejemplo de formato</summary>
          <pre style={{ background: "var(--bg-subtle)", padding: 12, borderRadius: 8, fontSize: 11, overflow: "auto", marginTop: 8 }}>{`[
  { "num": "POR-12", "name": "Vitinha", "role": "Mediocampo", "shirt": 12 },
  { "num": "POR-19", "name": "Cristiano Ronaldo", "is_legendary": true },
  { "num": "ARG-10", "name": "Lionel Messi", "is_legendary": true, "shirt": 10 }
]`}</pre>
        </details>
        <textarea
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='Pega tu JSON aquí…'
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
        />
        {result && (
          <div className="card" style={{ marginTop: 12, padding: 12, fontSize: 13 }}>
            <b>Resultado:</b> {result.updated} actualizadas · {result.skipped} sin cambios
            {result.errors.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: "pointer", color: "var(--accent-magenta)" }}>{result.errors.length} errores</summary>
                <ul style={{ paddingLeft: 18, margin: "6px 0 0", fontSize: 11 }}>
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
          <button type="button" className="btn btn-primary" disabled={working || !text.trim()} onClick={submit}>
            {working ? "Importando…" : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Tab de REPORTES (estadísticas) — % por país, top coleccionistas, láminas
// más buscadas / más en duplicado, resumen global.
// ============================================================================

function ReportsTab() {
  const [summary, setSummary] = useState<ReportsGlobalSummary | null>(null);
  const [byCountry, setByCountry] = useState<CountryReportRow[]>([]);
  const [top, setTop] = useState<TopCollectorRow[]>([]);
  const [needed, setNeeded] = useState<StickerReportRow[]>([]);
  const [duped, setDuped] = useState<StickerReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchReportsGlobalSummary(),
      fetchReportsByCountry(),
      fetchTopCollectors(20),
      fetchMostNeeded(20),
      fetchMostDuped(20),
    ])
      .then(([s, c, t, n, d]) => {
        setSummary(s);
        setByCountry(c);
        setTop(t);
        setNeeded(n);
        setDuped(d);
        setError(null);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (error) return <ErrorBox text={error} />;
  if (!summary) return null;

  const cards = [
    { label: "Usuarios activos", value: summary.users_active, color: "var(--primary)" },
    { label: "% promedio del álbum", value: `${summary.avg_album_pct}%`, sub: "global", color: "var(--accent-cobalt)" },
    { label: "Láminas pegadas total", value: summary.total_owned.toLocaleString("es-CO"), sub: `de ${summary.catalog_size} en el catálogo`, color: "var(--accent-magenta)" },
    { label: "Láminas repetidas", value: summary.total_dups.toLocaleString("es-CO"), sub: "disponibles para intercambio", color: "var(--accent-amber)" },
  ];

  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Resumen global */}
      <section>
        <h3 style={{ marginBottom: 8, fontSize: 16 }}>Resumen global</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
          {cards.map((c) => (
            <div key={c.label} className="card" style={{ borderTop: `3px solid ${c.color}` }}>
              <div className="stat-label" style={{ marginBottom: 6 }}>{c.label}</div>
              <div className="stat-value" style={{ fontSize: 28 }}>{c.value}</div>
              {c.sub && <div className="tiny muted" style={{ marginTop: 4 }}>{c.sub}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Por país */}
      <section>
        <h3 style={{ marginBottom: 8, fontSize: 16 }}>Por país</h3>
        {byCountry.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>Sin datos.</div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                  <Th>País</Th>
                  <Th>Usuarios</Th>
                  <Th>% promedio del álbum</Th>
                  <Th>Repetidas totales</Th>
                </tr>
              </thead>
              <tbody>
                {byCountry.map((r) => (
                  <tr key={r.country} style={{ borderTop: "1px solid var(--border)" }}>
                    <Td><b>{r.country}</b></Td>
                    <Td>{r.users_count}</Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, maxWidth: 160, height: 8, background: "var(--bg-subtle)", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${r.avg_album_pct}%`, height: "100%", background: "var(--primary)" }} />
                        </div>
                        <span style={{ minWidth: 50, textAlign: "right", fontWeight: 600 }}>{r.avg_album_pct}%</span>
                      </div>
                    </Td>
                    <Td>{r.total_dups.toLocaleString("es-CO")}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Top coleccionistas */}
      <section>
        <h3 style={{ marginBottom: 8, fontSize: 16 }}>Top coleccionistas</h3>
        {top.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>Sin datos.</div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                  <Th>#</Th>
                  <Th>Coleccionista</Th>
                  <Th>País</Th>
                  <Th>Pegadas</Th>
                  <Th>% Álbum</Th>
                </tr>
              </thead>
              <tbody>
                {top.map((u, i) => (
                  <tr key={u.user_id} style={{ borderTop: "1px solid var(--border)" }}>
                    <Td><b>{i + 1}</b></Td>
                    <Td>
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        {u.handle && <div className="tiny muted">{u.handle}</div>}
                      </div>
                    </Td>
                    <Td>{u.country}</Td>
                    <Td>{u.owned}</Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, maxWidth: 140, height: 6, background: "var(--bg-subtle)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${u.album_pct}%`, height: "100%", background: i === 0 ? "var(--accent-amber)" : "var(--primary)" }} />
                        </div>
                        <span style={{ minWidth: 40, textAlign: "right", fontWeight: 600 }}>{u.album_pct}%</span>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Más buscadas + más en duplicado side-by-side */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
        <section>
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>Más buscadas (les faltan a más users)</h3>
          {needed.length === 0 ? (
            <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>Sin datos.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                    <Th>#</Th><Th>Lámina</Th><Th>Equipo</Th><Th>Falta a</Th>
                  </tr>
                </thead>
                <tbody>
                  {needed.map((s, i) => (
                    <tr key={s.sticker_id} style={{ borderTop: "1px solid var(--border)" }}>
                      <Td>{i + 1}</Td>
                      <Td><b>{s.sticker_num}</b> · {s.sticker_name}</Td>
                      <Td className="tiny muted">{s.team_name}</Td>
                      <Td><span className="chip magenta">{s.users_count}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>Más en duplicado (las que sobran)</h3>
          {duped.length === 0 ? (
            <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)" }}>Sin datos.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                    <Th>#</Th><Th>Lámina</Th><Th>Equipo</Th><Th>Sobran a</Th>
                  </tr>
                </thead>
                <tbody>
                  {duped.map((s, i) => (
                    <tr key={s.sticker_id} style={{ borderTop: "1px solid var(--border)" }}>
                      <Td>{i + 1}</Td>
                      <Td><b>{s.sticker_num}</b> · {s.sticker_name}</Td>
                      <Td className="tiny muted">{s.team_name}</Td>
                      <Td><span className="chip amber">{s.users_count}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ============================================================================

function Th({ children }: { children: ReactNode }) {
  return <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--fg-muted)" }}>{children}</th>;
}

function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={className} style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>;
}

function Loading() {
  return <div style={{ padding: 32, textAlign: "center", color: "var(--fg-muted)" }}>Cargando…</div>;
}

function ErrorBox({ text }: { text: string }) {
  return <div className="card" style={{ borderLeft: "3px solid var(--accent-magenta)", marginTop: 16 }}>Error: {text}</div>;
}

function pct(num: number, denom: number): number {
  if (!denom) return 0;
  return Math.round((num / denom) * 100);
}

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
